import { useMemo, useRef, useState } from 'react';
import { useOutsideClick, useWindowResize } from '@rainbow-modules/hooks';
import { InternalOverlay } from 'react-rainbow-components';
import { PositionResolverOpts } from 'react-rainbow-components/components/InternalOverlay';
import ButtonIcon from 'components/ButtonIcon';
import Filter from 'components/icons/filter';
import useSearchFilters from 'hooks/useSearchFilters';
import { INVOICE_FILTER_NAMES } from '../../constants';
import { FilterButtonDropDown, StyledBadgeOverlay } from './styled';
import Filters from './filters';

const positionResolver = (opts: PositionResolverOpts) => {
    const { trigger, content, viewport } = opts;
    const triggerLeft = trigger.leftUpAnchor.x;
    const top = Math.max(0, trigger.leftUpAnchor.y + trigger.height + 5);
    const desiredLeft = Math.max(0, viewport.width - content.width - 14);
    const shouldAlignToTrigger = desiredLeft - triggerLeft > 0;
    const left = shouldAlignToTrigger
        ? trigger.rightBottomAnchor.x - content.width + 14
        : desiredLeft;
    return {
        top,
        left,
        bottom: 14,
    };
};

interface FilterButtonProps {
    label?: string;
    organizationId: string;
    filterNames?: string[];
    hideCustomerFilter?: boolean;
}

const FilterButton = ({
    label,
    organizationId,
    filterNames = INVOICE_FILTER_NAMES,
    hideCustomerFilter,
}: FilterButtonProps) => {
    const triggerRef = useRef<any>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    useWindowResize(() => setIsOpen(false), isOpen);
    useOutsideClick(
        dropdownRef,
        (event) => {
            const triggerEl = triggerRef.current?.buttonRef?.current;
            const target = event.target as Node | null;
            if (triggerEl && target && (target === triggerEl || triggerEl.contains(target))) {
                return;
            }

            const clickedOverlay = (event.target as HTMLElement | null)
                ?.closest?.('[role="presentation"], [role="dialog"]');
            const ownOverlay = dropdownRef.current?.closest('[role="presentation"]');
            if (clickedOverlay && clickedOverlay !== ownOverlay) {
                return;
            }

            setIsOpen(false);
        },
        isOpen,
    );

    const {
        getValue: getFilterValue,
    } = useSearchFilters({
        filterNames,
    });

    const activeFiltersCount = useMemo(
        () => filterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [filterNames, getFilterValue],
    );

    return (
        <StyledBadgeOverlay
            value={activeFiltersCount}
            isHidden={activeFiltersCount === 0}
            variant="brand"
            position="top-left"
        >
            <ButtonIcon
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                icon={<Filter />}
                title={label}
                borderRadius="semi-rounded"
                variant="base"
                className="rainbow-m-right_small"
                tooltip="Filter"
            />
            <InternalOverlay
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                positionResolver={positionResolver}
                isVisible={isOpen}
                triggerElementRef={() => triggerRef.current.buttonRef}
            >
                <FilterButtonDropDown ref={dropdownRef}>
                    <Filters
                        organizationId={organizationId}
                        filterNames={filterNames}
                        hideCustomerFilter={hideCustomerFilter}
                        onRequestClose={() => setIsOpen(false)}
                    />
                </FilterButtonDropDown>
            </InternalOverlay>
        </StyledBadgeOverlay>
    );
};

export default FilterButton;
